'use client'

import Image, { ImageProps } from 'next/image'

import { imgProxyLoader } from '~/utils/imgproxy'

import styles from './Carousel.module.css'
import { useCarousel } from './CarouselApi'

type Styles = { theme?: typeof styles }

function mergedTheme(id: string, theme: { readonly [key: string]: string } | undefined) {
  return `${styles[id]} ${theme?.[id]}`
}

function CarouselContent({ theme, ...props }: React.ComponentProps<'div'> & Styles) {
  const { carouselRef } = useCarousel()

  return (
    <div ref={carouselRef} data-slot='carousel-content' className={mergedTheme('carouselContent', theme)}>
      <div className={mergedTheme('carouselContentElement', theme)} {...props} />
    </div>
  )
}

function CarouselItem({ theme, ...props }: React.ComponentProps<'div'> & Styles) {
  return (
    <div role='group' aria-roledescription='slide' data-slot='carousel-item' className={mergedTheme('carouselItem', theme)} {...props} />
  )
}

function CarouselButton({ theme, isNext = false }: { isNext?: boolean } & Styles) {
  const { scrollPrev, canScrollPrev, scrollNext, canScrollNext } = useCarousel()

  const disabled = isNext ? canScrollNext : canScrollPrev

  const onClick = isNext ? scrollNext : scrollPrev

  const dataSlot = isNext ? 'carousel-next' : 'carousel-previous'

  return (
    <button data-slot={dataSlot} disabled={!disabled} onClick={onClick} className={mergedTheme('carouselButton', theme)}>
      <Image src={'/lp/assets/icons/arrow-left.svg'} alt='Icon arrow' width={40} height={40} />
    </button>
  )
}

function CarouselDots({ theme }: Styles) {
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useCarousel()

  return (
    <div className={mergedTheme('emblaDots', theme)}>
      {scrollSnaps.map((_, index) => (
        <button
          key={index}
          onClick={e => onDotButtonClick(e as unknown as MouseEvent, index)}
          className={`${mergedTheme('emblaDot', theme)} ${index === selectedIndex && mergedTheme('emblaDotSelected', theme)}`}
        />
      ))}
    </div>
  )
}

function CarouselControls({ theme }: Styles) {
  return (
    <>
      <div className={mergedTheme('carouselButtonsWrapper', theme)}>
        <CarouselButton />
        <CarouselButton isNext />
      </div>
      <CarouselDots />
    </>
  )
}

function CarouselImage({ src, alt, index = 0, ...props }: ImageProps & { index: number }) {
  return (
    <Image
      loader={imgProxyLoader}
      src={src}
      quality={80}
      alt={alt}
      objectFit='cover'
      priority={index === 0}
      loading={index === 0 ? 'eager' : 'lazy'}
      {...props}
    />
  )
}

export { CarouselButton, CarouselContent, CarouselControls, CarouselDots, CarouselImage, CarouselItem }
